import { RouteComponentProps } from '@reach/router';
import Page from '../../components/shared/Page';
import { UpdateSchool } from './UpdateSchool';
import { UpdateArchive } from './UpdateArchiveDate';
import placeholderMSAddImage from '../../images/Icon_ManageSchool_01.png';
import placeholderMSArchiveDateImage from '../../images/Icon_ManageSchool_02.png';
import React, { useCallback, useEffect, useState } from 'react';
import useFetchSchoolDetails from 'hooks/useFetchSchoolDetails';
import { ISchool } from './models';
import Tooltip from 'components/tooltip/Tooltip';
import { WondeLink } from '../../pages/wondeIntegration/WondeLink';
import { WondeIntegration } from '../../pages/wondeIntegration/WondeIntegration';
import { SyncWondeDataResponse } from '../wondeIntegration/models';
import { Subscriptions } from '../subscriptions/Subscriptions';
import { SchoolMfaApi } from 'api/schoolMfaApi';
import { MfaRequestStatus } from 'pages/mfa/models';
import { SchoolMfaPanel } from './SchoolMfaPanel';
import { IpWhitelistPanel } from './IpWhitelistPanel';

export const ManageSchool: React.FC<RouteComponentProps> = () => {
  const [updateIsOpen, setUpdateIsOpen] = useState(false);
  const [dateIsOpen, setDateIsOpen] = useState(false);
  const [schoolDetails, setSchoolDetails] = useState<ISchool | null>(null);
  const [schoolAddressLine, setSchoolAddressLine] = useState<string | null>(null);
  const [wondeIntegration, setWondeIntegration] = useState<boolean>(false);
  const [selectedSyncType, setSelectedSyncType] = useState<string>("ClassAndPupil");
  const [wondeSchoolId, setWondeSchoolId] = useState<any>(null);
  const [syncResponse, setSyncResponse] = useState<SyncWondeDataResponse | null>(null);
  const [mfaStatus, setMfaStatus] = useState<MfaRequestStatus | null>(null);

  const loadMfaStatus = useCallback(async () => {
    const result = await SchoolMfaApi.getMfaRequestStatus();
    if (result.data) setMfaStatus(result.data);
  }, []);

  useEffect(() => {
    loadMfaStatus();
  }, [loadMfaStatus]);

  // Only an explicit false hides the panels, so a status without the flag still shows them.
  const showSchoolMfaPanels = !!mfaStatus && mfaStatus.isMainAccountHolder !== false;

  useEffect(() => {
    if(!wondeIntegration && !updateIsOpen && !dateIsOpen){
      document.body.style.overflowY = '';
    }
  }, [wondeIntegration, updateIsOpen]);

  useFetchSchoolDetails(setSchoolDetails);

  useEffect(() => {
    // If the wonde integration is cancelled reset the sync response
    !wondeIntegration && setSyncResponse(null);
  }, [wondeIntegration]);

  useEffect(() => {
    if(schoolDetails){
      let schoolAddressLineStr = 
        `${schoolDetails?.addressLine2 ?? ''}
        ${
          schoolDetails?.addressLine2 && schoolDetails?.addressLine3
            ? ', '
            : ''
        }
        ${schoolDetails?.addressLine3 ?? ''}
        ${
          schoolDetails?.addressLine3 &&
          schoolDetails?.postcodeZipcode
            ? ', '
            : ''
        }
        ${schoolDetails?.postcodeZipcode ?? ''}
        ${
          schoolDetails?.postcodeZipcode && schoolDetails?.country
            ? ', '
            : ''
        }
        ${schoolDetails?.country ?? ''}`;

      if(schoolAddressLineStr){
        setSchoolAddressLine(schoolAddressLineStr);
      }
    }
  }, [schoolDetails]);  

  return (
    <Page>
      {wondeIntegration ? 
        <WondeIntegration 
          setWondeIntegration={setWondeIntegration}
          selectedSyncType={selectedSyncType}
          wondeSchoolId={wondeSchoolId}
          syncResponse={syncResponse}
        />
        :
        <div className="PageContent ManageSchoolPage">
          <div className="WidthContent HeightContent">
            <Subscriptions 
              schoolName={schoolDetails?.schoolName} 
              schoolURN={schoolDetails?.urn}
            />

            {showSchoolMfaPanels && mfaStatus && (
              <>
                <SchoolMfaPanel status={mfaStatus} onChanged={loadMfaStatus} />
                <IpWhitelistPanel />
              </>
            )}

            <div className="ManageSchoolRow">
              <div className="MSBlock Col1">
                <div className="MSDetailsRow">
                  <div className="DetailImg">                  
                    {schoolDetails && schoolDetails.logoUrl ?
                      <div 
                        className="ProfileSchooLogo"
                        style={{
                          backgroundImage: `url(${schoolDetails.logoUrl})`,
                          backgroundSize: "105%",
                          backgroundPosition: "center center",
                          backgroundRepeat: "no-repeat"
                        }}
                      />
                    :
                      <img src={placeholderMSAddImage} alt="Upload school logo icon" />
                    }

                    {schoolDetails && !schoolDetails.logoUrl && 
                      <button
                        className={`Btn UploadSchoolLogo`}
                        onClick={() => {
                          setUpdateIsOpen(true);
                        }}
                        disabled={schoolDetails ? false : true}
                      >
                        Upload school logo
                      </button>
                    } 
                  </div>
                  <div className="DetailTxt">
                    {schoolDetails?.addressLine1 && <h3 title={schoolDetails?.addressLine1}>{schoolDetails?.addressLine1 ?? ''}</h3>}
                    {schoolAddressLine && <p title={schoolAddressLine}>{schoolAddressLine}</p>}
                    <button 
                      className="Btn BtnWhiteGrey"
                      onClick={() => {
                        setUpdateIsOpen(true);
                      }}
                      disabled={schoolDetails ? false : true}                      
                    >
                      Update school details
                    </button>
                  </div>
                </div>
              </div>
              <div className="MSBlock Col2">
                <div className="MSDetailsRow">
                  <div className="DetailImg">
                    <img src={placeholderMSArchiveDateImage} alt="Archive data icon" />
                  </div>
                  <div className="DetailTxt">
                    <p className="NoOverflow">
                      Your data will archive on:
                      <Tooltip text="Archiving will move your assessment tables, pupil profile pages and your current curriculum map into your archive. Once archived, you can move and organise your classes for the next academic year. NB: do not move your classes before archiving to ensure no changes are made to your assessments." />
                    </p>
                    <h3>{schoolDetails?.yearlyArchivingDate ?? ''}</h3>
                    <button 
                      className="Btn BtnWhiteGrey"
                      onClick={() => {
                        setDateIsOpen(true);
                      }}
                      disabled={schoolDetails ? false : true}                    
                    >
                      Or archive now
                    </button>                    
                  </div>
                </div>
              </div>
            </div>

            <WondeLink
              setWondeIntegration={setWondeIntegration}
              selectedSyncType={selectedSyncType}
              setSelectedSyncType={setSelectedSyncType}
              wondeIntegration={wondeIntegration} 
              setWondeSchoolId={setWondeSchoolId}
              setSyncResponse={setSyncResponse}
              wondeApprovalGuide={!schoolDetails ? "" : schoolDetails?.wondeApprovalGuideURL}
              schoolURN={schoolDetails?.urn}
            />
          </div>

          {/* update school form */}
          {updateIsOpen && (
            <UpdateSchool
              panelOpen={updateIsOpen}
              togglePanelOpen={setUpdateIsOpen}
              schoolDetails={schoolDetails}
              setSchoolDetails={setSchoolDetails}
            />
          )}

          {/* update archiving date form */}
          {dateIsOpen && (
            <UpdateArchive
              panelOpen={dateIsOpen}
              togglePanelOpen={setDateIsOpen}
              setSchoolDetails={setSchoolDetails}
            />
          )}         
        </div>
      }
    </Page>
  );
};
